import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Mago L2-5 (manuale V1, rr. 4138-4270). 16 mosse.
 *
 * Pattern notabili:
 *  - **8 mosse richiedono *Lanciare Incantesimi Arcani*** (Mago da Battaglia,
 *    Protezione Arcana, Magia Potenziata, Terzo Occhio, Controincantesimo,
 *    e altre 3 nel pool L6-10 per chi sale).
 *  - **2 mosse richiedono *Corpus Arcano*** (Magnus Corpus Arcano, Famiglio).
 *  - **2 mosse richiedono *Mana*** (Talento Innato, Raschiare il Fondo del Barile).
 *  - ***Famiglio*** (manuale rr. 4187-4204): trascritta come `Move` con
 *    `choices` strutturate per le **6 peculiarità** (`choiceCount: 2`,
 *    "scegli 2 peculiarità"). Il sistema Seguaci a Ranghi 1-3 — di cui il
 *    Famiglio è un'istanza — è tracciato in **FI-8** e rinviato a Fase 2:
 *    nessun `HirelingSchema` viene introdotto in questa PR.
 *  - **Cross-class restriction in description** ("Se hai già preso questa
 *    mossa come Chierico, non puoi prenderla di nuovo!" su *Magia Potenziata*).
 *    Pattern testuale, FI-17 traccia il rifacimento strutturato in futuro.
 *  - ***Dilettante Magico***: mossa multiclasse standard, `tag: 'multiclasse'`.
 */

const formazioneEterogenea: Move = {
  id: 'mago.formazione-eterogenea',
  name: 'Formazione Eterogenea',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Se hai *Scudo Magico*, ottieni *Scuola per Giovani Maghi*, e viceversa.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede la mossa iniziale rimanente del gruppo
  // `mossa-iniziale-aggiuntiva` del Mago (Scudo Magico / Scuola per
  // Giovani Maghi — pool=1 residuo).
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const magoDaBattaglia: Move = {
  id: 'mago.mago-da-battaglia',
  name: 'Mago da Battaglia',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando plasmi la magia grezza in un incantesimo per danneggiare un nemico',
  description:
    "Tira come per *lanciare incantesimi*. Con 7+, infliggi 1d6 danni con etichetta *fisico*. Con 10+, scegli 3 opzioni dalla lista. Con 7-9, solo 2:\n\n- scegli un'etichetta di portata per l'attacco (*media* o *vicino*);\n- applichi l'etichetta *di [elemento]* (scegli il tipo) al colpo;\n- infliggi +1 danno (puoi scegliere questa opzione più volte);\n- applichi l'etichetta *impatto* al colpo;\n- non attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà.\n\nSe non scegli un'etichetta di portata, quella dell'attacco è *corta*.\n\nSe spendi 1 presa di *Mana* (e non più di una!), puoi scegliere 2 opzioni extra dall'elenco.\n\nSe hai *Protezione Arcana* e spendi una presa di *difendere* per causare danno, puoi infliggere 1d6 danni con etichetta *fisico* al posto del tuo dado di Danno.",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const protezioneArcana: Move = {
  id: 'mago.protezione-arcana',
  name: 'Protezione Arcana',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando *difendi* qualcuno o qualcosa',
  description:
    'Hai +2 Armatura contro gli attacchi magici. Inoltre, quando ***difendi*** **qualcuno o qualcosa**, puoi, se vuoi, tirare come per *lanciare incantesimi* invece che +**COS**.',
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const magnusCorpusArcano: Move = {
  id: 'mago.magnus-corpus-arcano',
  name: 'Magnus Corpus Arcano',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Aggiungi al tuo *Corpus Arcano* due incantesimi dalla lista di qualsiasi altra classe, che siano di livello pari o inferiore al tuo livello da Mago.',
  tags: [],
  prerequisites: ['mago.corpus-arcano'],
  replaces: [],
  // FI-33 (D2): manuale V1 r. 4168. Una sola grant `on-take`: 2 spell di
  // qualsiasi *altra* classe, livello ≤ livello-Mago. Il pool scala col
  // livello del PG: a L3 (prima opportunità tramite advanced_2_5) include
  // L0+L1+L3 altre classi; a L9 include tutto.
  grantsSpells: [
    {
      trigger: 'on-take',
      count: 2,
      levelRule: { kind: 'lte-character-level' },
      source: { kind: 'other-classes' },
    },
  ],
};

const talentoInnato: Move = {
  id: 'mago.talento-innato',
  name: 'Talento Innato',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando usi la mossa *Mana*',
  description:
    'Prendi un numero di prese pari al tuo più alto livello da incantatore +3 (massimo 13).',
  tags: [],
  prerequisites: ['mago.mana'],
  replaces: [],
};

const magiaPotenziata: Move = {
  id: 'mago.magia-potenziata',
  name: 'Magia Potenziata',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando tiri per *lanciare incantesimi*',
  description:
    "Con 10+, se accetti di subire una complicazione dalla lista del 7-9, scegli un potenziamento tra questi (se applicabile, e con conseguenze a discrezione del GM):\n\n- gli effetti numerici sono massimizzati, gli altri potenziati;\n- l'effetto interessa un numero doppio di bersagli o un'area più vasta.\n\nGli effetti si applicano sia che tu usi *lanciare incantesimi* o un'altra mossa che richieda di «*Tirare come per* lanciare incantesimi».\n\nSe hai già questa mossa come Chierico, non puoi prenderla di nuovo!",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const famiglio: Move = {
  id: 'mago.famiglio',
  name: 'Famiglio',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger:
    'Quando dedichi alcuni minuti a compiere il rituale di evocazione o di animazione della creatura (alla presa della mossa, scegli 2 peculiarità tra quelle elencate)',
  description:
    'Hai appreso un rituale per evocare una creatura o dar vita a un costrutto che ti serve fedelmente. Anche se non sa parlare, hai stabilito con esso una basilare forma di comunicazione (niente concetti complessi). Il *Famiglio* obbedisce sempre ai tuoi comandi verbali, senza dover *dare ordini a un gregario*.\n\nScegli **due peculiarità** dalle opzioni elencate.\n\nQuando raggiunge 0 PF, la creatura sparisce e dovrai compiere un nuovo rituale per richiamarla a te.',
  tags: [],
  prerequisites: ['mago.corpus-arcano'],
  replaces: [],
  followerTemplateId: 'mago-famiglio',
  // FI-8b (D1.5): 4 delle 6 peculiarità sono **cumulative** se prese 2×.
  // Le altre 2 (linguaggio, telepatia) sono binarie: la UI le mostra come
  // checkbox standard, le 4 cumulative come stepper "+/-".
  choices: [
    {
      id: 'adepto-rango-2',
      label: "Ha l'Abilità Adepto a Rango 2",
      description: 'Rango 3 se scelta 2 volte.',
      allowDuplicate: true,
    },
    {
      id: 'altra-abilita-rango-1',
      label: "Ha un'altra Abilità a Rango 1",
      description: 'Rango 2 se scelta 2 volte (stessa Abilità).',
      allowDuplicate: true,
    },
    {
      id: 'modificatore-piu-2',
      label: 'Ha un modificatore di Caratteristica a +2',
      description: '+3 se scelta 2 volte (stessa Caratteristica).',
      allowDuplicate: true,
    },
    {
      id: 'parla-linguaggio-comune',
      label: 'Sa parlare un linguaggio comprensibile ai più',
    },
    {
      id: 'telepatia',
      label: 'Siete in grado di comunicare telepaticamente tra voi',
    },
    {
      id: 'capacita-utile',
      label: 'Ha una capacità utile',
      description: 'Concordala con il GM. Se scelta 2 volte, sono 2 capacità separate.',
      allowDuplicate: true,
    },
  ],
  choiceCount: 2,
  narrativeFields: [
    {
      id: 'nome-famiglio',
      label: 'Nome del Famiglio',
      placeholder: 'es. Krelth, Verdezampa, Sussurro…',
      required: true,
      multiline: false,
    },
    {
      id: 'descrizione-famiglio',
      label: 'Descrizione del Famiglio',
      placeholder:
        'Forma (animale, costrutto, spirito), aspetto, materiali (se costrutto), segni distintivi…',
      required: false,
      multiline: true,
    },
    // FI-8b: prompt enum-condizionati per le 3 peculiarità che richiedono
    // di specificare un valore runtime (quale Abilità, quale stat). Visibili
    // solo se la rispettiva peculiarità è selezionata almeno 1×.
    {
      id: 'quale-altra-abilita',
      label: 'Quale Abilità per la peculiarità «altra Abilità»',
      placeholder: 'Seleziona…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'altra-abilita-rango-1' },
      oneOf: [
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
    },
    {
      id: 'quale-modificatore',
      label: 'Quale Caratteristica per il modificatore +2 / +3',
      placeholder: 'Seleziona…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'modificatore-piu-2' },
      oneOf: ['forza', 'destrezza', 'costituzione', 'intelligenza', 'saggezza', 'carisma'],
    },
    // FI-8b: capacità custom — 2 coppie label/desc con visibilità
    // count-aware. La 1ª coppia appare se "capacita-utile" è scelta 1+ volta,
    // la 2ª solo se 2 volte.
    {
      id: 'capacita-utile-label-1',
      label: 'Capacità utile — nome',
      placeholder: 'es. Vede al buio, Cammina sui muri…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 1 },
    },
    {
      id: 'capacita-utile-desc-1',
      label: 'Capacità utile — descrizione (opzionale)',
      placeholder: 'Spiegazione concordata con il GM…',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 1 },
    },
    {
      id: 'capacita-utile-label-2',
      label: '2ª Capacità utile — nome',
      placeholder: 'es. Sensi acuti, Resistenza al fuoco…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 2 },
    },
    {
      id: 'capacita-utile-desc-2',
      label: '2ª Capacità utile — descrizione (opzionale)',
      placeholder: 'Spiegazione concordata con il GM…',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 2 },
    },
  ],
};

const terzoOcchio: Move = {
  id: 'mago.terzo-occhio',
  name: 'Terzo Occhio',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: "Quando lanci e mantieni l'incantesimo *Individuazione del Magico*",
  description:
    'Per te *Individuazione del Magico* diventa un incantesimo *continuato*. Mentre lo mantieni, prendi -1 a *lanciare incantesimi*, ma puoi *discernere realtà* riguardo ciò che di magico ti circonda e aggiungere «*Qual è la natura o la funzione di questa magia/oggetto magico?*» alla lista di domande che puoi fare.',
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const controincantesimo: Move = {
  id: 'mago.controincantesimo',
  name: 'Controincantesimo',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger:
    'Quando vuoi provare ad annullare, prima che si manifesti, un incantesimo che viene lanciato entro il tuo campo visivo',
  description:
    "Se il tuo livello da Incantatore e le tue prese di *Mana* residue sono almeno pari al livello dell'incantesimo che vuoi contrastare (a discrezione del GM), tira come per *lanciare incantesimi*. Nota che le prese di *Mana* le stai solo rischiando, non spendendo!\n\nCon 10+, l'incantesimo ostile viene cancellato. Con 7-9, lo stesso, ma devi anche scegliere un'opzione dalla lista 7-9 della mossa che usi per *lanciare incantesimi*.\n\nIl livello di incantesimo per questa mossa è lo stesso di quello contrastato.",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const raschiareIlFondoDelBarile: Move = {
  id: 'mago.raschiare-il-fondo-del-barile',
  name: 'Raschiare il Fondo del Barile',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando attingi a tutte le tue energie, anche a quelle più nascoste',
  description:
    'Prendi un numero di prese di *Mana* pari a metà del tuo più alto livello da Incantatore (arrotondando per eccesso). Se lo fai, però, subisci un -1 continuato a qualsiasi tiro finché non riesci a goderti una notte di riposo ininterrotto.\n\nNon puoi avere più prese di *Mana* del massimo consentito per il tuo livello da Incantatore e non puoi usare nuovamente questa abilità finché la penalità derivante dal suo utilizzo resta attiva.',
  tags: [],
  prerequisites: ['mago.mana'],
  replaces: [],
};

const vediamoComeFunzioni: Move = {
  id: 'mago.vediamo-come-funzioni',
  name: 'Vediamo come Funzioni',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando smonti un congegno meccanico per capirne il funzionamento',
  description:
    "Tira+**INT**. Con 7+ fai due delle domande seguenti al GM (che deve rispondere sinceramente):\n\n- cosa fa?\n- chi l'ha creato?\n- cosa c'è che non va, e come posso sistemarlo?\n- qual è la cosa che è stata fatta più di recente con o a questo oggetto?\n\nInoltre, con 10+, scegli tre. Con 7-9, scegli due:\n\n- non ci metti molto tempo;\n- non attiri l'attenzione, né ti metti nei guai;\n- puoi rimetterlo a posto senza troppo sforzo;\n- recuperi qualcosa di valore.",
  roll: {
    stat: 'intelligenza',
    on10plus: { text: '2 domande dalla lista informativa + scegli 3 opzioni dalla seconda lista.' },
    on7to9: { text: '2 domande dalla lista informativa + scegli 2 opzioni dalla seconda lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const analisiRapida: Move = {
  id: 'mago.analisi-rapida',
  name: 'Analisi Rapida',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando vedi manifestarsi un incantesimo arcano',
  description:
    "Chiedi al GM il nome di quell'incantesimo e quali sono di preciso i suoi effetti. Prendi +1 al prossimo tiro che fai sfruttando quello che hai saputo.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const apprendimentoArcano: Move = {
  id: 'mago.apprendimento-arcano',
  name: 'Apprendimento Arcano',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger:
    'Quando *declami conoscenze* o *discerni la realtà* riguardo qualcosa di magico o arcano',
  description:
    'Sei una sorgente di conoscenza esoterica. Con un risultato di 10+, il GM ti dirà anche un segreto poco conosciuto riguardo quel qualcosa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const logico: Move = {
  id: 'mago.logico',
  name: 'Logico',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger:
    'Quando *discerni la realtà*, non affidandoti alla tua intuizione o al tuo istinto, ma analizzando ciò che ti circonda con logica e deduzione',
  description: 'Puoi tirare+**INT** invece che +**SAG**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const soTuttoIo: Move = {
  id: 'mago.so-tutto-io',
  name: 'So Tutto Io',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando un altro PG ti chiede esplicitamente consiglio su qualcosa di importante',
  description:
    'Egli ottiene +1 al prossimo tiro che fa seguendo i tuoi suggerimenti. Se mette davvero in pratica i tuoi consigli, tu segni un PE (ma non più di una singola volta a sessione).',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const dilettanteMagico: Move = {
  id: 'mago.dilettante-magico',
  name: 'Dilettante Magico',
  type: 'advanced_2_5',
  classId: 'mago',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Ottieni una mossa non multiclasse dalla lista di una qualunque classe. Scegli la mossa come se fossi di un livello inferiore al tuo, a meno che quella mossa non sia basata sulla magia.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1.
  // **NB**: l'eccezione «a meno che quella mossa non sia basata sulla magia»
  // è qualitativa nel manuale (description-only). Il catalogo non ha
  // un tag formale `'magia'`, quindi il delta si applica uniformemente.
  // Estensibile in futuro via `levelAdjustment.exceptions.moveTags: ['magia']`.
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: { delta: -1 },
  },
};

export const magoAdvanced2to5Moves: readonly Move[] = [
  formazioneEterogenea,
  magoDaBattaglia,
  protezioneArcana,
  magnusCorpusArcano,
  talentoInnato,
  magiaPotenziata,
  famiglio,
  terzoOcchio,
  controincantesimo,
  raschiareIlFondoDelBarile,
  vediamoComeFunzioni,
  analisiRapida,
  apprendimentoArcano,
  logico,
  soTuttoIo,
  dilettanteMagico,
] as const;
